<template>
  <div>
    <LoadingState v-if="!status.isLoaded" :size="50" />
    <div v-if="status.isLoaded && server">
      <PageTitle>
        <div class="mr-2">{{ server.host }}</div>
        <div class="server-attributes d-md-inline-flex">
          <div class="attribute">
            <div class="field">{{ $t("global.status") }}</div>
            <div class="value">
              <Pill
                :content="$t(`global.${server.status}`)"
                :variant="getStatusVariant(server.status)"
              />
            </div>
          </div>
          <div class="attribute">
            <div class="field">{{ $t("global.plan") }}</div>
            <div class="value">{{ server.description }}</div>
          </div>
          <div class="attribute">
            <div class="field">{{ $t("global.ip-address") }}</div>
            <div class="value">
              {{ server.mainIp }}
              <CopyToClipboard :text="server.mainIp" />
            </div>
          </div>
        </div>
        <template #actions>
          <DropDown :items="menuItems()">
            <Btn>{{ $t("global.actions") }}</Btn>
          </DropDown>
        </template>
      </PageTitle>
      <div class="row">
        <div class="col-md-12 mb-4">
          <tab :tabs="getTabs()">
            <template #transfer>
              <CloudServerTransfer :server="server" />
            </template>
            <template #log>
              <CloudServerLog :server="server" />
            </template>
            <template #history>
              <CloudServerHistory :server="server" />
            </template>
          </tab>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import {
  getCdnControlPanelLogin,
  getConsole,
  getContactSupport,
  getDeleteService,
  getHardReboot,
  getNewRelicLogin,
  getPurgeCache,
  getResize,
  getSoftReboot,
  getStart,
  getStop,
  getToggleEdgecastApac,
  getToggleEdgecastSharedSsl,
  getToggleNetworking,
  getUpdateReverseDns,
} from "@/utils/serviceMenuOptions";
import Btn from "@/components/display/Btn";
import CloudServerHistory from "@/components/smart/CloudServerHistory";
import CloudServerLog from "@/components/smart/CloudServerLog";
import CloudServerTransfer from "@/components/smart/CloudServerTransfer";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import DropDown from "@/components/display/DropDown";
import { getStatusVariant } from "@/utils/statusVariant";
import LoadingState from "@/components/display/LoadingState";
import PageTitle from "@/components/display/PageTitle";
import Pill from "@/components/display/Pill";
import store from "@/store";
import Tab from "@/components/display/Tab";

export default {
  name: "CloudServerDetailPage",
  components: {
    Btn,
    CopyToClipboard,
    CloudServerHistory,
    CloudServerLog,
    CloudServerTransfer,
    DropDown,
    LoadingState,
    PageTitle,
    Pill,
    Tab,
  },
  props: {
    cloudServerId: [Number, String],
  },
  setup(props, { root }) {
    const server = computed(() => store.state.cloudServers.item);
    const status = computed(() => store.state.cloudServers.status);

    onMounted(() => {
      store.dispatch("cloudServers/get", props.cloudServerId);
    });

    const getTabs = () => {
      return [
        { name: "transfer", label: root.$t("global.transfer") },
        { name: "log", label: root.$t("global.console-log") },
        { name: "history", label: root.$t("global.history") },
      ];
    };

    const menuItems = () => {
      const items = [];
      items.push(
        getCdnControlPanelLogin(server.value),
        getConsole(server.value),
        getContactSupport(server.value),
        getDeleteService(server.value),
        getHardReboot(server.value),
        getNewRelicLogin(server.value),
        getPurgeCache(server.value),
        getResize(server.value),
        getSoftReboot(server.value),
        getStart(server.value),
        getStop(server.value),
        getToggleEdgecastApac(server.value),
        getToggleEdgecastSharedSsl(server.value),
        getToggleNetworking(server.value),
        getUpdateReverseDns(server.value)
      );

      return items.filter((i) => i !== null);
    };

    return {
      getStatusVariant,
      getTabs,
      menuItems,
      server,
      status,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";

.server-attributes {
  font-size: 0.875rem;
  .attribute {
    display: flex;
    align-items: baseline;
    @include media-breakpoint-up(md) {
      &:not(:last-child) {
        &::after {
          margin-right: 1rem;
          margin-left: 1rem;
          content: "|";
        }
      }
    }
    .field {
      font-size: 0.75rem;
      margin-right: 0.5rem;
    }
    .value {
      font-weight: bold;
    }
  }
}
</style>
